import { ThemeProvider } from '@emotion/react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Typography
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useRecoilValue } from 'recoil'

import { Wrapper, AppBarR as PersistentDrawerRight } from '../components/common'
import { sessionState } from '../state'
import lightTheme from '../theme'

import routes from './routes'

const HelpPage = () => {
  const session = useRecoilValue(sessionState)
  const navigate = useNavigate()
  const isLoggedIn = !!session.user

  const helpContent = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        py: 4
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
          <HelpOutlineIcon fontSize='small' />
        </Avatar>
        <Typography component='h1' variant='h6' sx={{ fontWeight: 600 }}>
          Getting Started Guide
        </Typography>
      </Box>

      <Typography
        variant='body1'
        sx={{ mb: 4, color: 'text.secondary', fontSize: '1.1rem' }}
      >
        Welcome to Meet for Work! Follow these steps to get started with
        managing your office spaces and bookings.
      </Typography>

      {/* Step 1 */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Step 1: Create Your Office and Desks
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            Start by setting up your workspace. Navigate to the{' '}
            <strong>Desks</strong> menu from the sidebar and click "Create Desk"
            to add your first desk.
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', lineHeight: 1.7 }}
          >
            <strong>Tip:</strong> When you create a desk in a new office name,
            the office will be automatically created for you. You'll become the
            office manager with full control over desks, invitations, and
            settings.
          </Typography>
        </CardContent>
      </Card>

      {/* Step 2 */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Step 2: Invite Your Team
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            Share your office with colleagues by going to the{' '}
            <strong>Offices</strong> menu, selecting your office, and clicking
            "Invite Someone". Enter their email address to send an invitation.
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', lineHeight: 1.7 }}
          >
            <strong>Important:</strong> Invited users must sign up using the
            exact email address you sent the invitation to. Once they register,
            they'll automatically have access to book desks in your office.
          </Typography>
        </CardContent>
      </Card>

      {/* Step 3 */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Step 3: Book a Desk
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            Ready to reserve your workspace? Click on any day in the calendar on
            the home screen, or use the <strong>Book</strong> menu to create a
            booking.
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 1, color: 'text.secondary', lineHeight: 1.7 }}
          >
            <strong>Booking options:</strong>
          </Typography>
          <Box component='ul' sx={{ mt: 0, pl: 3, color: 'text.primary' }}>
            <li>Select your start date</li>
            <li>Choose duration (single day or multiple days/weeks)</li>
            <li>Pick your office and preferred desk</li>
            <li>View available desks in real-time</li>
          </Box>
          <Typography
            variant='body2'
            sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            <strong>Note:</strong> You can cancel your own bookings anytime by
            clicking the delete icon on the calendar.
          </Typography>
        </CardContent>
      </Card>

      {/* Step 4 */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Step 4: View Team Schedules
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            The home screen calendar shows you who's coming to the office and
            when. Each day displays all bookings with color-coded office
            indicators.
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', lineHeight: 1.7 }}
          >
            <strong>Calendar features:</strong>
          </Typography>
          <Box component='ul' sx={{ mt: 1, pl: 3, color: 'text.primary' }}>
            <li>View 4 weeks at a time (28 days)</li>
            <li>See all bookings for each day</li>
            <li>Hover over names to see desk details</li>
            <li>Navigate forward/backward by 4-week periods</li>
            <li>Past dates are greyed out for clarity</li>
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Subscription Tiers */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Subscription Tiers & User Limits
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            Each office has a subscription tier that determines how many users
            can be invited. Choose the plan that fits your team size.
          </Typography>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Free Tier
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
            >
              Up to 5 users • Perfect for small teams • All core features
              included
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Small Office - $29/month
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
            >
              Up to 20 users • Priority support • All features
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Medium Office - $59/month
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
            >
              Up to 50 users • Priority support • Advanced analytics
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography
              variant='body2'
              sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
            >
              Large Office - $199/month
            </Typography>
            <Typography
              variant='body2'
              sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
            >
              Up to 200 users • Dedicated account manager • All premium features
            </Typography>
          </Box>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            How to Upgrade Your Office
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 1, color: 'text.secondary', lineHeight: 1.7 }}
          >
            When you reach your user limit, you'll see a notification prompting
            you to upgrade. To upgrade:
          </Typography>
          <Box component='ol' sx={{ mt: 1, pl: 3, color: 'text.primary' }}>
            <li>Go to the Offices menu and select your office</li>
            <li>
              Click "Upgrade Plan" or view the subscription status section
            </li>
            <li>Choose your new tier from the pricing modal</li>
            <li>Complete the secure payment process</li>
            <li>Your new user limit takes effect immediately</li>
          </Box>
          <Typography
            variant='body2'
            sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            <strong>Note:</strong> You can also access pricing from the
            navigation menu. Office managers can upgrade or downgrade plans at
            any time.
          </Typography>
        </CardContent>
      </Card>

      {/* Office Notifications */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Office Notifications
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            Office managers can create notifications that appear on specific
            dates in the calendar. This is perfect for communicating important
            information to your team.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Creating Notifications
          </Typography>
          <Box
            component='ol'
            sx={{ mt: 1, mb: 2, pl: 3, color: 'text.primary' }}
          >
            <li>Navigate to "Notifications" from the menu</li>
            <li>Select the office and date for your notification</li>
            <li>Write your message (supports markdown formatting)</li>
            <li>Click "Add Notification" to save</li>
          </Box>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Viewing Notifications
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            Days with notifications show a bell icon in the calendar. Click the
            icon to view the notification in a modal. Notifications support
            basic markdown including bold text, headings, and links.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Common Uses
          </Typography>
          <Box component='ul' sx={{ mt: 1, pl: 3, color: 'text.primary' }}>
            <li>Office closures or holidays</li>
            <li>Building maintenance schedules</li>
            <li>Team events or meetings</li>
            <li>Important policy updates</li>
            <li>Desk availability changes</li>
          </Box>
        </CardContent>
      </Card>

      {/* Profile Page */}
      <Card sx={{ mb: 3, boxShadow: 2 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Your Profile Page
          </Typography>
          <Typography
            variant='body1'
            sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
          >
            Access your profile from the navigation menu to view your account
            information and customize your experience.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Profile Information
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            Your profile displays your name, email address, and lists all
            offices you're a member of. Each office shows your role (Manager or
            Member) with a color-coded badge.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Theme Preferences
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            Switch between light and dark themes to match your preference or
            reduce eye strain. Your theme choice is saved automatically and
            applies across all your devices.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Office Roles
          </Typography>
          <Box component='ul' sx={{ mt: 1, pl: 3, color: 'text.primary' }}>
            <li>
              <strong>Manager:</strong> Can create/edit desks, invite users,
              create notifications, and manage subscriptions
            </li>
            <li>
              <strong>Member:</strong> Can book desks and view office
              information
            </li>
          </Box>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      {/* Additional Features */}
      <Card sx={{ mb: 3, boxShadow: 2, bgcolor: 'action.hover' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography
            variant='h6'
            sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
          >
            Additional Tips
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Desk Management
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            Edit or delete desks from the Desks menu. You can update desk names,
            add labels for easy identification, or restrict desk availability to
            specific users.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Recurring Bookings
          </Typography>
          <Typography
            variant='body2'
            sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
          >
            When booking a desk, you can select multiple days or weeks at once.
            This is perfect for booking your regular office days in advance.
          </Typography>

          <Typography
            variant='body2'
            sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
          >
            Calendar Navigation
          </Typography>
          <Typography
            variant='body2'
            sx={{ color: 'text.secondary', lineHeight: 1.7 }}
          >
            The calendar always starts on Sunday and shows 4 weeks (28 days) at
            a time. Use the navigation arrows to jump forward or backward by
            4-week periods. Past dates appear greyed out for clarity.
          </Typography>
        </CardContent>
      </Card>

      {/* Need Help */}
      <Box
        sx={{
          mt: 2,
          p: 3,
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography
          variant='body2'
          sx={{ color: 'text.secondary', textAlign: 'center' }}
        >
          Need more help? Contact your office administrator or check your
          profile settings for additional options.
        </Typography>
      </Box>
    </Box>
  )

  // If logged in, show with the authenticated wrapper
  if (isLoggedIn) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default'
        }}
      >
        <PersistentDrawerRight></PersistentDrawerRight>
        <Wrapper>
          <Container component='main' maxWidth='md'>
            {helpContent}
          </Container>
        </Wrapper>
      </Box>
    )
  }

  // If logged out, wrap in light theme so MUI components render with light colors
  // matching the light-colored inline styles of the public header
  return (
    <ThemeProvider theme={lightTheme}>
      <div
        style={{
          minHeight: '100vh',
          backgroundColor: '#f8fafc',
          fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif'
        }}
      >
        {/* Simple public header */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e2e8f0',
            padding: '16px 32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            position: 'sticky',
            top: 0,
            zIndex: 100
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/')}
            sx={{
              color: '#2563eb',
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem'
            }}
          >
            Back to Home
          </Button>
          <Button
            variant='contained'
            onClick={() => navigate(routes.signup)}
            sx={{
              backgroundColor: '#2563eb',
              borderRadius: '8px',
              textTransform: 'none',
              fontWeight: 600,
              px: 3
            }}
          >
            Get Started
          </Button>
        </div>

        <Container component='main' maxWidth='md'>
          <Box sx={{ display: 'flex', flexDirection: 'column', py: 4 }}>
            <Typography
              variant='body1'
              sx={{ mb: 4, color: 'text.secondary', fontSize: '1.1rem' }}
            >
              Welcome to your desk booking system! Follow these steps to get
              started with managing your office spaces and bookings.
            </Typography>

            {/* Step 1 */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Step 1: Create Your Office and Desks
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Start by setting up your workspace. Navigate to the{' '}
                  <strong>Offices</strong> page from the sidebar and click
                  &quot;Create Office&quot; to add your first office.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Once your office is created, open it to access the office
                  detail page where you can add desks. Click &quot;Add
                  Desk&quot; to create desks with names and optional labels
                  (e.g., &quot;Window&quot;, &quot;Standing Desk&quot;,
                  &quot;Quiet Zone&quot;).
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>Tip:</strong> You will automatically become the office
                  owner with full control over desks, invitations, and settings.
                  You can also assign office managers who can help manage desks
                  and invitations.
                </Typography>
              </CardContent>
            </Card>

            {/* Step 2 */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Step 2: Invite Your Team
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Share your office with colleagues by going to the{' '}
                  <strong>Offices</strong> page, selecting your office, and
                  clicking &quot;Invite Someone&quot;. Enter their email address
                  to send an invitation.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>Important:</strong> Invited users must sign up using
                  the exact email address you sent the invitation to. Once they
                  register, they will automatically have access to book desks in
                  your office.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>My Team:</strong> You can also build personal team
                  connections via the <strong>My Team</strong> feature (accessed
                  from your <strong>Profile</strong> page). Team connections
                  allow you to book desks on behalf of team members and view
                  their schedules. See the My Team section below for details.
                </Typography>
              </CardContent>
            </Card>

            {/* Step 3 */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Step 3: Book a Desk
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Ready to reserve your workspace? Use the <strong>Book</strong>{' '}
                  page from the navigation menu to create a booking.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 1, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>Booking modes:</strong>
                </Typography>
                <Box
                  component='ul'
                  sx={{ mt: 0, pl: 3, color: 'text.primary' }}
                >
                  <li>
                    <strong>Once off:</strong> Book 1 to 5 consecutive days
                    using the day selector buttons (1d, 2d, 3d, 4d, 5d)
                  </li>
                  <li>
                    <strong>Same days every week:</strong> Select specific
                    weekdays (Sun through Sat) using chip selectors to create
                    recurring bookings for approximately 3 months
                  </li>
                </Box>
                <Typography
                  variant='body2'
                  sx={{
                    mt: 2,
                    mb: 1,
                    color: 'text.secondary',
                    lineHeight: 1.7
                  }}
                >
                  <strong>Booking for others:</strong>
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
                >
                  Use the &quot;Booking For&quot; dropdown to book a desk on
                  behalf of a team member. This feature is available when you
                  have team connections set up via My Team.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{
                    mt: 2,
                    mb: 1,
                    color: 'text.secondary',
                    lineHeight: 1.7
                  }}
                >
                  <strong>Office Layout tab:</strong>
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
                >
                  When an office has a visual layout configured, a second
                  &quot;Office Layout&quot; tab appears alongside the
                  &quot;Desks&quot; tab on the Book page. This provides an
                  interactive map showing desk locations.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>Note:</strong> You can cancel your own bookings
                  anytime by clicking the delete icon on the calendar.
                </Typography>
              </CardContent>
            </Card>

            {/* Step 4 */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Step 4: View Team Schedules
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  The <strong>Home</strong> page displays your booking calendar
                  with a toggle to switch between views.
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 1, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>View toggle:</strong>
                </Typography>
                <Box
                  component='ul'
                  sx={{ mt: 0, pl: 3, color: 'text.primary' }}
                >
                  <li>
                    <strong>My Bookings:</strong> Shows your personal upcoming
                    bookings across all offices
                  </li>
                  <li>
                    <strong>My Team Bookings:</strong> Shows upcoming bookings
                    made by your team members, allowing you to see who is in the
                    office and when
                  </li>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>Tip:</strong> You can delete bookings you made on
                  behalf of team members directly from the team bookings view.
                </Typography>
              </CardContent>
            </Card>

            <Divider sx={{ my: 4 }} />

            {/* My Team */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  My Team
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Build personal team connections to enable proxy bookings and
                  team schedule visibility. Access My Team from your{' '}
                  <strong>Profile</strong> page by clicking the &quot;My
                  Team&quot; button.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Invite Team Members
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Send team invitations by entering a colleague&apos;s email
                  address. They will receive an invitation that they can accept
                  or reject.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Pending Invitations
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  View invitations in two tabs: <strong>Received</strong>{' '}
                  (invitations others have sent you) and <strong>Sent</strong>{' '}
                  (invitations you have sent). Accept or reject received
                  invitations as needed.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Manage Members
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  View your current team members and remove connections when
                  needed (with a confirmation dialog). Team connections are
                  mutual and enable:
                </Typography>
                <Box
                  component='ul'
                  sx={{ mt: 0, pl: 3, color: 'text.primary' }}
                >
                  <li>
                    Booking desks on behalf of team members (proxy bookings)
                  </li>
                  <li>Viewing team members&apos; bookings on the Home page</li>
                </Box>
              </CardContent>
            </Card>

            {/* Office Layout */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Office Layout
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Offices can have a visual floor plan that shows desk locations
                  in an interactive layout.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Viewing the Layout
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  When an office has a layout configured, the{' '}
                  <strong>Book</strong> page shows an &quot;Office Layout&quot;
                  tab alongside the &quot;Desks&quot; tab. The layout provides a
                  read-only interactive view with a fullscreen option for easier
                  navigation.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Creating a Layout
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Office managers and owners can create or edit the office
                  layout via the Edit Office page. The drawing tool allows you
                  to position desks on a visual floor plan that all office
                  members can view.
                </Typography>
              </CardContent>
            </Card>

            {/* Subscription Tiers */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Subscription Tiers and Billing
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Each office has a subscription tier that determines how many
                  users can be invited. Choose the plan that fits your team
                  size.
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    Free Tier
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
                  >
                    Up to 5 users - $0/month - All core features included
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    Small Office - $29/month
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
                  >
                    Up to 20 users - Priority support - All features
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    Medium Office - $59/month
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
                  >
                    Up to 50 users - Priority support - Advanced analytics
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                  >
                    Large Office - $199/month
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: 'text.secondary', lineHeight: 1.7, pl: 2 }}
                  >
                    Up to 200 users - Dedicated account manager - All premium
                    features
                  </Typography>
                </Box>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Annual Billing
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Save 10% by choosing annual billing on any paid plan. Switch
                  between monthly and annual billing when upgrading or managing
                  your subscription.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  How to Upgrade Your Office
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 1, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  When you reach your user limit, you will see a notification
                  prompting you to upgrade. To upgrade:
                </Typography>
                <Box
                  component='ol'
                  sx={{ mt: 1, pl: 3, color: 'text.primary' }}
                >
                  <li>Go to the Offices page and select your office</li>
                  <li>
                    Click &quot;Upgrade Plan&quot; or view the subscription
                    status section
                  </li>
                  <li>Choose your new tier from the pricing modal</li>
                  <li>Select monthly or annual billing</li>
                  <li>Complete the secure payment process</li>
                  <li>Your new user limit takes effect immediately</li>
                </Box>
                <Typography
                  variant='body2'
                  sx={{ mt: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  <strong>Note:</strong> You can also access pricing from the
                  navigation menu when the Pricing feature is enabled. Office
                  managers and owners can upgrade or downgrade plans at any
                  time.
                </Typography>
              </CardContent>
            </Card>

            {/* Office Notifications */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Office Notifications
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Office managers can create announcements that appear on
                  specific dates. Access the <strong>Notifications</strong> page
                  from the navigation menu.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Creating Notifications
                </Typography>
                <Box
                  component='ol'
                  sx={{ mt: 1, mb: 2, pl: 3, color: 'text.primary' }}
                >
                  <li>Navigate to &quot;Notifications&quot; from the menu</li>
                  <li>Select the office and date for your notification</li>
                  <li>Write your message (supports markdown formatting)</li>
                  <li>Click &quot;Add Notification&quot; to save</li>
                </Box>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Common Uses
                </Typography>
                <Box
                  component='ul'
                  sx={{ mt: 1, pl: 3, color: 'text.primary' }}
                >
                  <li>Office closures or holidays</li>
                  <li>Building maintenance schedules</li>
                  <li>Team events or meetings</li>
                  <li>Important policy updates</li>
                </Box>
              </CardContent>
            </Card>

            {/* Profile Page */}
            <Card sx={{ mb: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Your Profile
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ mb: 2, color: 'text.primary', lineHeight: 1.7 }}
                >
                  Access your profile from the navigation menu to view your
                  account information and manage team connections.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Profile Information
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Your profile displays your name, email address, and lists all
                  offices you belong to. Each office shows your role (Manager or
                  Member) with a color-coded badge. Clicking on an office
                  navigates to its detail page.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  My Team Button
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Click the &quot;My Team&quot; button on your Profile page to
                  manage your personal team connections. This navigates to the
                  My Team page where you can invite, view, and manage team
                  members.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Theme Preferences
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Toggle between light and dark themes using the &quot;Toggle
                  dark mode&quot; option in the navigation menu. Your theme
                  choice is saved automatically.
                </Typography>
              </CardContent>
            </Card>

            <Divider sx={{ my: 4 }} />

            {/* Additional Tips */}
            <Card sx={{ mb: 3, boxShadow: 2, bgcolor: 'action.hover' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 700, mb: 2, color: 'primary.main' }}
                >
                  Additional Tips
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Office Management
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Manage all your offices from the <strong>Offices</strong>{' '}
                  page. From each office detail page, you can add or edit desks,
                  invite users, manage subscriptions, and configure the office
                  layout. Office owners and managers can also delete offices
                  they control.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Recurring Bookings
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ mb: 2, color: 'text.secondary', lineHeight: 1.7 }}
                >
                  Use the &quot;Same days every week&quot; mode to set up
                  recurring reservations. Select the weekdays you want (e.g.,
                  Monday and Wednesday) and the system will create bookings for
                  approximately 3 months.
                </Typography>

                <Typography
                  variant='body2'
                  sx={{ fontWeight: 600, mb: 1, color: 'text.primary' }}
                >
                  Calendar Navigation
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ color: 'text.secondary', lineHeight: 1.7 }}
                >
                  The Home page calendar shows 4 weeks (28 days) at a time. Use
                  the navigation arrows to move forward or backward. Past dates
                  appear greyed out for clarity.
                </Typography>
              </CardContent>
            </Card>

            {/* Need Help */}
            <Box
              sx={{
                mt: 2,
                p: 3,
                bgcolor: 'background.paper',
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Typography
                variant='body2'
                sx={{ color: 'text.secondary', textAlign: 'center' }}
              >
                Need more help? Contact your office administrator or check your
                profile settings for additional options.
              </Typography>
            </Box>
          </Box>
        </Container>
      </div>
    </ThemeProvider>
  )
}

export default HelpPage
